import { Factory } from 'fishery'
import {
  type OpalApiComponent,
  type OpalApiResponseDocument,
} from '@opallabs/api'
import { workflowResourceFactory } from '../resource_objects/workflow'
import { workflowAssignmentResourceFactory } from '../resource_objects/workflow_assignment'
import { workflowResponseResourceFactory } from '../resource_objects/workflow_response'
import { workflowStageResourceFactory } from '../resource_objects/workflow_stage'

type ReadWorkflowResponseV3 = OpalApiResponseDocument<'ReadWorkflowV3', 200>
type Progress = ReadWorkflowResponseV3['data']['attributes']['progress']
type StageType = OpalApiComponent<
  'V3',
  'workflow_stage'
>['attributes']['stage_type']
type ResponseDetails = OpalApiComponent<
  'V3',
  'workflow_response'
>['attributes']['details']

export type ReadWorkflowAssignmentSpec = {
  /** The assigned user's UUID. */
  user: string | null
  responses?: Array<{
    details: ResponseDetails
    /** The responding user's UUID. */
    by: string
    /** RFC 3339 date-time */
    at: string
  }>
}

type ReadWorkflowTransientParams = {
  /** @default 'tasks_pending' */
  progress: Progress
  /** @default 'task' */
  stageType: StageType
  /** @default null */
  dueDate: string | null
  /** @default 'Stage' */
  stageTitle: string
  assignments: ReadWorkflowAssignmentSpec[]
  /**
   * The types of the stages after the current stage.
   *
   * @default []
   */
  laterStageTypes: StageType[]
}

/**
 * One workflow as `GET /v3/workflows/{id}?include=stages,current_stage.assignments.responses` returns it: a current
 * stage with the given assignments and their responses, then the later stages.
 */
export const readWorkflowFactory: Factory<
  ReadWorkflowResponseV3,
  ReadWorkflowTransientParams
> = Factory.define<ReadWorkflowResponseV3, ReadWorkflowTransientParams>(
  ({
    transientParams: {
      progress = 'tasks_pending',
      stageType = 'task',
      dueDate = null,
      stageTitle = 'Stage',
      assignments = [],
      laterStageTypes = [],
    },
  }) => {
    const stage = workflowStageResourceFactory.build({
      attributes: {
        stage_type: stageType,
        due_date: dueDate,
        metadata: { title: stageTitle },
        position: 0,
      },
    })
    const laterStages = laterStageTypes.map((type, index) =>
      workflowStageResourceFactory.build({
        attributes: { stage_type: type, position: index + 1 },
      })
    )
    const built = assignments.map(({ user, responses = [] }) => {
      const assignment = workflowAssignmentResourceFactory.build({
        attributes: { assignment_type: stageType },
        relationships: {
          assigned_user: { data: user ? { id: user, type: 'user' } : null },
          stage: { data: { id: stage.id, type: 'workflow_stage' } },
        },
      })
      const builtResponses = responses.map(({ details, by, at }) =>
        workflowResponseResourceFactory.build({
          attributes: { details, created_at: at },
          relationships: {
            assignment: {
              data: { id: assignment.id, type: 'workflow_assignment' },
            },
            created_by_user: { data: { id: by, type: 'user' } },
          },
        })
      )
      assignment.relationships.responses.data = builtResponses.map(
        ({ id, type }) => ({ id, type })
      )
      return { assignment, responses: builtResponses }
    })
    stage.relationships.assignments.data = built.map(
      ({ assignment: { id, type } }) => ({ id, type })
    )
    const workflow = workflowResourceFactory.build({
      attributes: { progress, is_approved: progress === 'approved' },
      relationships: {
        current_stage: { data: { id: stage.id, type: 'workflow_stage' } },
        stages: {
          data: [stage, ...laterStages].map(({ id, type }) => ({ id, type })),
        },
      },
    })
    return {
      data: workflow,
      included: [
        stage,
        ...built.flatMap(({ assignment, responses }) => [
          assignment,
          ...responses,
        ]),
        ...laterStages,
      ],
    }
  }
)
